import clsx from 'clsx'
import styles from '../ControlSheetCells.module.scss'
import { ControlSheetAnnotation, ControlSheetNote, ControlSheetTrip } from '../../types/types'
import { DutyTooltip } from '../../ui/DutyTooltip'
import { MealReliefModal } from '../../ui/MealReliefModal'
import { DualTooltipMealRelief } from '../../ui/DualTooltipMealRelief'
import { AnnotationsPermission } from '@/pages/ControlSheet/ui/ControlSheetPage'
import { standardizeTime } from '../../helpers/standardizeTime'
import { displayTime } from '../../helpers/displayTime'
import { timeToMinutes } from '../../helpers/breakpointTimeUtils'
import ChatIcon from '../../assets/chat.svg'
import EditIcon from '../../assets/edit.svg'
import Clock from '../../../../pages/ControlSheet/assets/clock.svg'

interface Props {
    trip: ControlSheetTrip
    run: string
    route: string
    mealRelief: number
    annotationsPermission: AnnotationsPermission
    addTempNote: (note: ControlSheetNote) => void
    deleteTempNote: (noteId: string) => void
    addTempAnnotation: (annotation: ControlSheetAnnotation) => void
}

export const LayoverMealReliefCell = ({
    trip,
    run,
    route,
    mealRelief,
    annotationsPermission,
    addTempNote,
    deleteTempNote,
    addTempAnnotation,
}: Props) => {
    const nextTripRoute = trip.routeAfterBreak && trip.routeAfterBreak !== route ? trip.routeAfterBreak : undefined

    // Find meal relief annotation
    const annotation = trip.annotations.find((it) => it.annotationType === 'MealRelief')

    // Calculate break times, accounting for any annotation overrides
    let breakEndTime = trip.breakEnd
    let breakStartTime = trip.breakStart

    if (annotation?.departureTime) {
        breakEndTime = annotation.departureTime
    }
    if (annotation?.startTime) {
        breakStartTime = annotation.startTime
    }

    const breakTimeMinutes =
        timeToMinutes(standardizeTime(breakEndTime)) - timeToMinutes(standardizeTime(breakStartTime))

    // Build the text content similar to breakpointMapper
    const clock =
        breakTimeMinutes < mealRelief ? (
            <span style={{ paddingTop: 'var(--size-2)' }}>
                <Clock />
            </span>
        ) : null

    let text: React.ReactNode
    if (annotation?.startTime && annotation.startTime !== trip.breakStart) {
        text = (
            <span
                style={{
                    display: 'flex',
                    columnGap: 'var(--size-4)',
                    rowGap: 0,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                }}
            >
                {clock}
                <span className={styles.newTime}>{displayTime(standardizeTime(breakStartTime))}</span>
                <span style={{ flexBasis: '100%', height: 0 }} />
                {!isNaN(breakTimeMinutes) && <> ({breakTimeMinutes} min)</>}
            </span>
        )
    } else {
        text = (
            <span
                style={{
                    display: 'flex',
                    columnGap: 'var(--size-4)',
                    rowGap: 0,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                }}
            >
                {clock}
                {displayTime(standardizeTime(trip.breakStart))}
                <span style={{ flexBasis: '100%', height: 0 }} />
                {!isNaN(breakTimeMinutes) && <> ({breakTimeMinutes} min)</>}
            </span>
        )
    }

    const hoverTooltipComponent = (
        <DutyTooltip route={route} trip={trip} annotationsPermission={annotationsPermission} />
    )

    const clickTooltipComponent = (
        <MealReliefModal
            duty={trip.duty}
            trip={trip.trip}
            run={run}
            route={route}
            scheduled={trip.arriveStandTime}
            index={-1} // Special index for layover meal relief
            reliefStartScheduled={trip.breakStart}
            nextTrip={trip.tripAfterBreak ?? ''}
            nextTripScheduled={trip.breakEnd}
            nextTripRoute={nextTripRoute}
            minMealRelief={mealRelief}
            addTempNote={addTempNote}
            deleteTempNote={deleteTempNote}
            addTempAnnotation={addTempAnnotation}
            annotation={annotation}
        />
    )

    const child = (
        <div
            className={clsx(styles.cell, {
                [styles.cellEdit]: annotationsPermission === 'edit',
                [styles.mealReliefTime]: true,
            })}
        >
            {text}

            {annotation && annotation.notes.length > 0 && (
                <div className={styles.commentIcon}>
                    <ChatIcon />
                </div>
            )}

            <div className={styles.editIcon}>
                <EditIcon />
            </div>
        </div>
    )

    return (
        <DualTooltipMealRelief
            tooltip={hoverTooltipComponent}
            clickTooltip={clickTooltipComponent}
            trigger={annotationsPermission === 'edit' ? 'hover+click' : 'hover'}
            placement="bottom"
        >
            {child}
        </DualTooltipMealRelief>
    )
}
